import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { generateReportPDF, buildSummary } from '../report.js';
import { PHASE_LABEL } from '../phases.js';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function fmt(iso) {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function ReportPage() {
  const { user } = useAuth();
  const [from, setFrom] = useState(daysAgoISO(90));
  const [to, setTo] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [periodsRes, episodesRes, historyRes, statsRes] = await Promise.all([
        api.get('/cycle'),
        api.get(`/headache?from=${from}&to=${to}`),
        api.get(`/medication/history?from=${from}&to=${to}`),
        api.get('/cycle/stats').catch(() => null),
      ]);
      const periods = (periodsRes.periods || []).filter(
        (p) =>
          (p.startDate >= from && p.startDate <= to) ||
          (p.endDate && p.endDate >= from && p.endDate <= to)
      );
      const summary = buildSummary({
        episodes: episodesRes.episodes || [],
        intakes: historyRes.entries || [],
        periods,
      });
      setData({
        periods,
        episodes: episodesRes.episodes || [],
        intakes: historyRes.entries || [],
        cycleStats: statsRes,
        summary,
      });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function downloadPDF() {
    if (!data) return;
    const doc = generateReportPDF({
      user,
      from,
      to,
      periods: data.periods,
      episodes: data.episodes,
      intakes: data.intakes,
      cycleStats: data.cycleStats,
    });
    const filename = `diario-mal-di-testa_${from}_${to}.pdf`;
    doc.save(filename);
  }

  function setRange(daysBack) {
    setFrom(daysAgoISO(daysBack));
    setTo(todayISO());
  }

  return (
    <div>
      <div className="card">
        <h2>Report</h2>
        <p className="muted">
          Genera un PDF con riassunto, tabelle e diario giornaliero pronto da inviare al medico.
        </p>
        <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label>Dal</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label>Al</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="secondary" onClick={() => setRange(30)}>30g</button>
          <button className="secondary" onClick={() => setRange(90)}>90g</button>
          <button className="secondary" onClick={() => setRange(180)}>6 mesi</button>
          <button className="secondary" onClick={() => setRange(365)}>1 anno</button>
        </div>
        <div className="row" style={{ marginTop: '0.5rem' }}>
          <button onClick={load} disabled={loading} className="secondary">
            {loading ? 'Aggiorno…' : 'Aggiorna anteprima'}
          </button>
          <button onClick={downloadPDF} disabled={loading || !data}>
            Scarica PDF
          </button>
        </div>
        {err && <div className="error" style={{ marginTop: '0.5rem' }}>{err}</div>}
      </div>

      {data && (
        <>
          <div className="card">
            <h2>Anteprima riassunto</h2>
            <p className="muted">
              Periodo {fmt(from)} – {fmt(to)}
            </p>
            <ul className="list">
              <li>Episodi di mal di testa: <strong>{data.summary.totalEpisodes}</strong></li>
              <li>
                Intensit&agrave; media:{' '}
                <strong>{data.summary.totalEpisodes ? `${data.summary.avgIntensity}/10` : '—'}</strong>
              </li>
              <li>Assunzioni di farmaci: <strong>{data.summary.totalIntakes}</strong></li>
              <li>Periodi mestruali nel range: <strong>{data.summary.totalPeriods}</strong></li>
              {data.cycleStats && (
                <>
                  <li>Lunghezza media ciclo: <strong>{data.cycleStats.cycleLength} giorni</strong></li>
                  <li>
                    Prossimo previsto: <strong>{fmt(data.cycleStats.nextPeriodEstimate)}</strong>
                    {' · '}ovulazione: <strong>{fmt(data.cycleStats.nextOvulationEstimate)}</strong>
                  </li>
                </>
              )}
            </ul>
          </div>

          {Object.keys(data.summary.byPhase).length > 0 && (
            <div className="card">
              <h2>Mal di testa per fase del ciclo</h2>
              <ul className="list">
                {Object.entries(data.summary.byPhase).map(([k, v]) => (
                  <li key={k}>
                    {PHASE_LABEL[k] || k}: <strong>{v}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Object.keys(data.summary.intakesByMed).length > 0 && (
            <div className="card">
              <h2>Assunzioni per medicina</h2>
              <ul className="list">
                {Object.entries(data.summary.intakesByMed)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <li key={k}>
                      {k}: <strong>{v}</strong>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {data.summary.topTriggers.length > 0 && (
            <div className="card">
              <h2>Trigger ricorrenti</h2>
              <ul className="list">
                {data.summary.topTriggers.map(([k, v]) => (
                  <li key={k}>
                    {k}: <strong>{v}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
